import { apiService } from "./api";
import { PaginatedResponse } from "../types";

export interface DeliveryArea {
    delivery_area_id: string;
    name: string;
    delivery_charge: number;
    free_above_amount?: number | null;
    note?: string | null;
    is_active: boolean;
    display_order: number;
    create_time: string;
    update_time: string;
}

export interface DeliveryAreaCreate {
    name: string;
    delivery_charge: number;
    free_above_amount?: number | null;
    note?: string | null;
    is_active: boolean;
    display_order: number;
}

export type DeliveryAreaUpdate = Partial<DeliveryAreaCreate>;

export const deliveryAreaService = {
    async getDeliveryAreas(
        skip: number = 0,
        limit: number = 500,
        activeOnly: boolean = false,
    ): Promise<PaginatedResponse<DeliveryArea>> {
        return apiService.get<PaginatedResponse<DeliveryArea>>(
            `/delivery-areas?skip=${skip}&limit=${limit}&active_only=${activeOnly}`,
        );
    },

    async getDeliveryArea(id: string): Promise<DeliveryArea> {
        return apiService.get<DeliveryArea>(`/delivery-areas/${id}`);
    },

    async createDeliveryArea(data: DeliveryAreaCreate): Promise<DeliveryArea> {
        return apiService.post<DeliveryArea>("/delivery-areas", data);
    },

    async updateDeliveryArea(
        id: string,
        data: DeliveryAreaUpdate,
    ): Promise<DeliveryArea> {
        return apiService.put<DeliveryArea>(`/delivery-areas/${id}`, data);
    },

    async deleteDeliveryArea(id: string): Promise<void> {
        return apiService.delete<void>(`/delivery-areas/${id}`);
    },
};

// Active delivery areas, loaded once per session — they rarely change.
let activeAreasCache: Promise<DeliveryArea[]> | null = null;

/** Active delivery areas in their display order (cached for the session). */
export function loadActiveAreas(): Promise<DeliveryArea[]> {
    activeAreasCache ??= deliveryAreaService
        .getDeliveryAreas(0, 500, true)
        .then((res) => [...(res.items ?? [])].sort((a, b) => a.display_order - b.display_order))
        .catch((err) => {
            activeAreasCache = null;
            throw err;
        });
    return activeAreasCache;
}
